/**
 * Shared session marker cookie so the marketing site (getolliai.com) can
 * detect when a user is logged in on app.getolliai.com and redirect them
 * back to the dashboard.
 *
 * Security note: this cookie is a PRESENCE MARKER ONLY (value: "1"). The
 * actual JWT stays in localStorage, scoped to app.getolliai.com origin,
 * and is never shared across subdomains. Marketing site can't impersonate
 * a user from this cookie.
 */

const COOKIE_NAME = 'olli_session';

// Possible domain attributes a cookie might have been set with — both with
// and without leading dot, plus the current host. We try ALL of them when
// clearing so a stale cookie set under one variant can't survive logout.
function candidateDomains() {
    if (typeof window === 'undefined') return [''];
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return [''];
    const parts = host.split('.');
    const apex = parts.slice(-2).join('.'); // "getolliai.com"
    return ['', host, apex, '.' + apex];
}

function defaultDomain() {
    if (typeof window === 'undefined') return '';
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return '';
    const parts = host.split('.');
    return '.' + parts.slice(-2).join('.');
}

export function setSessionCookie() {
    if (typeof document === 'undefined') return;
    const domain = defaultDomain();
    const maxAge = 60 * 60 * 24 * 7; // 7 days
    const parts = [
        `${COOKIE_NAME}=1`,
        'path=/',
        `max-age=${maxAge}`,
        'SameSite=Lax',
    ];
    if (domain) parts.push(`domain=${domain}`);
    if (window.location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
}

export function clearSessionCookie() {
    if (typeof document === 'undefined') return;
    // Try every plausible domain attribute the cookie could have been
    // stored under. Browsers treat each as a distinct cookie, so wiping
    // them all is the only way to be sure the marker is gone.
    for (const domain of candidateDomains()) {
        const parts = [
            `${COOKIE_NAME}=`,
            'path=/',
            'expires=Thu, 01 Jan 1970 00:00:00 GMT',
            'max-age=0',
            'SameSite=Lax',
        ];
        if (domain) parts.push(`domain=${domain}`);
        document.cookie = parts.join('; ');
    }
}

/**
 * Returns true if the marker cookie is present right now.
 */
export function hasSessionCookie() {
    if (typeof document === 'undefined') return false;
    return document.cookie
        .split(';')
        .map((c) => c.trim())
        .some((c) => c.startsWith(`${COOKIE_NAME}=1`));
}

/**
 * Self-heal: if a stale cookie is present but localStorage has no token,
 * the user is effectively logged out — wipe the cookie. This handles cases
 * where logout failed or where the user opened /login directly.
 */
export function reconcileSession() {
    if (typeof document === 'undefined') return;
    const hasCookie = hasSessionCookie();
    const hasToken = !!localStorage.getItem('token');
    if (hasCookie && !hasToken) {
        clearSessionCookie();
    } else if (!hasCookie && hasToken) {
        setSessionCookie();
    }
}
